import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Paper,
  Stack,
  Chip,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { formatDateField } from "../../../../../../base/FormatTime";
import { sections } from "./fireRiskAssessment.config";

const Section = ({ title, children }) => (
  <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5 }}>
    <Typography
      variant="subtitle1"
      component="h3"
      sx={{ fontWeight: "bold", mb: 2, color: "text.primary" }}
    >
      {title}
    </Typography>
    {children}
  </Paper>
);

const DetailItem = ({ label, value, isLast = false }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      py: 1.5,
      borderBottom: !isLast ? 1 : 0,
      borderColor: "divider",
    }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", flex: 1 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: "bold",
        color: value === "Yes" ? "error.main" : "text.primary",
        textAlign: "right",
      }}
    >
      {value || "N/A"}
    </Typography>
  </Box>
);

const getChoiceLabel = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "Not answered";
};

const FireRiskAssessmentDetailsModal = ({ open, handleClose, assessment }) => {
  if (!assessment) return null;

  const getRiskColor = (level) => {
    switch (level) {
      case "High":
        return "error";
      case "Medium":
        return "warning";
      case "Low":
        return "success";
      default:
        return "default";
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div">
            Fire Risk Assessment Details
          </Typography>
          <IconButton onClick={handleClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: "grey.50", p: 3 }}>
        <Stack spacing={2}>
          <Section title="General Information">
            <DetailItem label="Service User" value={assessment.client_name} />
            <DetailItem
              label="Date of Assessment"
              value={formatDateField(assessment.created_at)}
            />
            <DetailItem label="Created By" value={assessment.created_by_name} />
            <DetailItem
              label="Care Plan"
              isLast
              value={assessment.care_plan_name}
            />
          </Section>

          {sections.map((section) => (
            <Section key={section.title} title={section.title}>
              {section.questions.map((q, index) => (
                <DetailItem
                  key={q.key}
                  label={q.label}
                  value={
                    typeof assessment[q.key] === "boolean"
                      ? getChoiceLabel(assessment[q.key])
                      : assessment[q.key]
                  }
                  isLast={index === section.questions.length - 1}
                />
              ))}
            </Section>
          ))}

          <Section title="Final Assessment">
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Overall Risk Level
              </Typography>
              <Chip
                label={assessment.risk_level}
                color={getRiskColor(assessment.risk_level)}
                sx={{ fontWeight: "bold" }}
              />
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Comment & Recommendation
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
              >
                {assessment.comment_recommendation || "No comments provided."}
              </Typography>
            </Box>
          </Section>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FireRiskAssessmentDetailsModal;
